import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ShieldCheck, FileSpreadsheet, BadgeCheck, Globe2, Sparkles } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import OtpRequestForm from "../components/auth/OtpRequestForm";
import OtpVerifyForm  from "../components/auth/OtpVerifyForm";
import "../styles/auth.css";

const TRUST_BADGES = [
  { icon: ShieldCheck,     label: "OTP Secured" },
  { icon: FileSpreadsheet, label: "GST-Native" },
  { icon: BadgeCheck,      label: "Certificate Ready" },
  { icon: Globe2,          label: "4 Countries" },
];

function StepDot({ number, label, state }) {
  return (
    <div className={`auth-step ${state}`}>
      <div className="auth-step-dot">{state === "completed" ? "✓" : number}</div>
      <span className="auth-step-label">{label}</span>
    </div>
  );
}

export default function LoginPage() {
  const { isAuthenticated } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  const [tab,   setTab]   = useState("login"); // "login" | "signup"
  const [step,  setStep]  = useState(1);
  const [email, setEmail] = useState("");

  function handleEmailSuccess(confirmedEmail) {
    setEmail(confirmedEmail);
    setStep(2);
  }

  function handleBack() {
    setStep(1);
    setEmail("");
  }

  function handleTabChange(t) {
    setTab(t);
    setStep(1);
    setEmail("");
  }

  const isSignup = tab === "signup";

  return (
    <div className="auth-shell">
      <div className="auth-card">

        {/* Header */}
        <div className="auth-card-header">
          <img src="/rs-logo.png" alt="GreenSetu" />
          <h1>GreenSetu</h1>
          <p>Climate Intelligence for MSMEs, Lenders &amp; Advisors</p>
        </div>

        {/* Tab toggle */}
        <div className="auth-tabs">
          <button
            className={`auth-tab${tab === "login" ? " active" : ""}`}
            onClick={() => handleTabChange("login")}
          >
            Sign In
          </button>
          <button
            className={`auth-tab${tab === "signup" ? " active" : ""}`}
            onClick={() => handleTabChange("signup")}
          >
            <Sparkles size={13} /> Get Started Free
          </button>
        </div>

        {/* Body */}
        <div className="auth-card-body">

          {/* Sign-up welcome message */}
          {isSignup && step === 1 && (
            <div className="auth-signup-banner">
              <BadgeCheck size={15} />
              <div>
                <strong>No credit card required.</strong> Start with 2 free screening runs instantly.
              </div>
            </div>
          )}

          {/* Step indicator */}
          <div className="auth-steps">
            <StepDot number={1} label="Your email"  state={step === 1 ? "active" : "completed"} />
            <StepDot number={2} label="Verify OTP"  state={step === 2 ? "active" : step > 2 ? "completed" : ""} />
          </div>

          {step === 1 && (
            <OtpRequestForm
              onSuccess={handleEmailSuccess}
              ctaLabel={isSignup ? "Create Account →" : "Send OTP →"}
            />
          )}
          {step === 2 && (
            <OtpVerifyForm email={email} onBack={handleBack} />
          )}

          {/* Trust badges */}
          <div className="auth-trust">
            {TRUST_BADGES.map(({ icon: Icon, label }) => (
              <span key={label} className="auth-trust-badge">
                <Icon size={11} /> {label}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="auth-footer">
          By continuing you agree to our{" "}
          <a href="https://rsustain.org/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {" "}·{" "}
          <Link to="/">← Back to home</Link>
        </div>

      </div>
    </div>
  );
}
